import React, { Suspense, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, Link, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Sidebar } from './components/layout/Sidebar';
import { Login } from './features/auth/Login';
import { AuthCallback } from './features/auth/AuthCallback';
import { EsqueciSenha } from './features/auth/EsqueciSenha';
import { AtualizarSenha } from './features/auth/AtualizarSenha';
import { MobileNav } from './components/layout/MobileNav';
import { MobileProfileMenu } from './components/layout/MobileProfileMenu';
import { LoadingScreen } from './components/ui/Loading';
import { analytics } from './services/analyticsService';

import { CreatorsModal } from './components/modals/CreatorsModal';
import { FeedbackModal } from './components/modals/FeedbackModal';
import { CompleteProfileModal } from './components/modals/CompleteProfileModal';
import { WelcomeOnboardingModal } from './components/modals/WelcomeOnboardingModal';
import { PricingModal } from './components/modals/PricingModal';
import { PlanComplianceModal } from './components/modals/PlanComplianceModal';
import { useSubscription } from './hooks/useSubscription';
import { useSyncUsageTelemetry } from './hooks/useSyncUsageTelemetry';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { Logo } from './components/ui/Logo';
import { Toaster } from 'react-hot-toast';

// Lazy load feature components for performance
const Dashboard = React.lazy(() => import('./features/dashboard/Dashboard').then(module => ({ default: module.Dashboard })));
const PersonasList = React.lazy(() => import('./features/personas/PersonasList').then(module => ({ default: module.PersonasList })));
const PersonaEditorPage = React.lazy(() => import('./features/personas/PersonaEditorPage').then(module => ({ default: module.PersonaEditorPage })));
const Contexts = React.lazy(() => import('./features/contexts/Contexts').then(module => ({ default: module.Contexts })));
const ContextEditorPage = React.lazy(() => import('./features/contexts/ContextEditorPage').then(module => ({ default: module.ContextEditorPage })));
const ConversationsHub = React.lazy(() => import('./features/chat/ConversationsHub').then(module => ({ default: module.ConversationsHub })));
const Arena = React.lazy(() => import('./features/chat/Arena').then(module => ({ default: module.Arena })));
const Settings = React.lazy(() => import('./features/settings/Settings').then(module => ({ default: module.Settings })));
const InsightsPage = React.lazy(() => import('./features/insights/InsightsPage').then(module => ({ default: module.InsightsPage })));

const ProtectedRoute = () => {
  const { session, loading, profile } = useAuth();
  const { isExpired, isSpecialGrant, isLoading: subscriptionLoading } = useSubscription();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCreatorsOpen, setIsCreatorsOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isCompleteProfileOpen, setIsCompleteProfileOpen] = useState(false);

  // Sincronização contínua e silenciosa da telemetria de uso (PQL) no HubSpot
  useSyncUsageTelemetry();

  const location = useLocation();
  const mainRef = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  if (loading || subscriptionLoading || (session && !profile)) {
    return <LoadingScreen message="Iniciando sessão..." />;
  }
  if (!session) return <Navigate to="/login" replace />;

  // Se a conta estiver com o trial ou assinatura expirada (e não for VIP), bloqueia 100% o acesso e renderiza apenas o Paywall obrigatório
  if (isExpired && !isSpecialGrant) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-br from-slate-100 via-indigo-50/40 to-slate-200 flex items-center justify-center relative overflow-hidden">
        {/* Luzes decorativas ambientes de fundo para compor o efeito de blur */}
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-indigo-300/25 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-violet-300/25 rounded-full blur-3xl pointer-events-none" />
        
        <PricingModal 
          isOpen={true} 
          isMandatory={true} 
          onClose={() => {}} 
        />
      </div>
    );
  }

  const isArena = location.pathname.includes('/chat/');

  return (
    <div className="bg-slate-900 w-full" style={{ height: '100dvh' }}>
      <div className="flex bg-slate-50 overflow-hidden mx-auto w-full max-w-[1920px] shadow-2xl relative" style={{ height: '100dvh' }}>
        <Sidebar 
          isOpen={sidebarOpen} 
          onClose={() => setSidebarOpen(false)} 
          onOpenCreators={() => setIsCreatorsOpen(true)} 
          onOpenFeedback={() => setIsFeedbackOpen(true)} 
          onOpenCompleteProfile={() => setIsCompleteProfileOpen(true)}
        />
        <main 
          ref={mainRef}
          className={`flex-1 flex flex-col relative md:pb-0 ${isArena ? 'overflow-hidden pb-0' : 'pb-[calc(72px+env(safe-area-inset-bottom))] overflow-y-auto'}`}
        >
        {/* Mobile Header Static Branding */}
        <div className="md:hidden py-3 px-4 bg-slate-900 border-b border-slate-800 flex items-center justify-between sticky top-0 z-30 shadow-md shrink-0">
          <Link to="/dashboard" className="focus:outline-none rounded-lg active:scale-[98%] transition-transform">
            <Logo theme="dark" size="standard" />
          </Link>
          <MobileProfileMenu 
            onOpenCreators={() => setIsCreatorsOpen(true)} 
            onOpenFeedback={() => setIsFeedbackOpen(true)} 
            onOpenCompleteProfile={() => setIsCompleteProfileOpen(true)}
          />
        </div>

        <ErrorBoundary>
          <Suspense fallback={<LoadingScreen />}>
            <div className="flex-1 flex flex-col relative w-full h-full animate-in fade-in duration-300">
              <Outlet />
            </div>
          </Suspense>
        </ErrorBoundary>

        <WelcomeOnboardingModal />
        <CompleteProfileModal isOpen={isCompleteProfileOpen} onClose={() => setIsCompleteProfileOpen(false)} />
        <CreatorsModal isOpen={isCreatorsOpen} onClose={() => setIsCreatorsOpen(false)} />
        <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
        <PlanComplianceModal />
        {!isArena && <MobileNav />}
      </main>
      </div>
    </div>
  );
};

const PageTracker = () => {
  const location = useLocation();

  React.useEffect(() => {
    analytics.pageView(location.pathname + location.search, document.title);
  }, [location.pathname, location.search]);

  return null;
};

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" />
      <Router>
        <PageTracker />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/cadastro" element={<Login />} />
          <Route path="/signup" element={<Login />} />
          <Route path="/esqueci-a-senha" element={<EsqueciSenha />} />
          <Route path="/atualizar-senha" element={<AtualizarSenha />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route element={<ProtectedRoute />}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />

            {/* Personas Routes */}
            <Route path="/personas" element={<PersonasList />} />
            <Route path="/personas/novo" element={<PersonaEditorPage />} />
            <Route path="/personas/:id" element={<PersonaEditorPage />} />

            {/* Contexts Routes */}
            <Route path="/contextos" element={<Contexts />} />
            <Route path="/contextos/novo" element={<ContextEditorPage />} />
            <Route path="/contextos/:id" element={<ContextEditorPage />} />

            {/* Conversations Routes */}
            <Route path="/conversas" element={<ConversationsHub />} />
            <Route path="/conversas/chat/:id" element={<Arena />} />

            {/* Insights Routes */}
            <Route path="/insights" element={<InsightsPage />} />

            <Route path="/configuracoes" element={<Settings />} />
          </Route>
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
